import React, { FC, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslate } from '@/translations';
import { useEventHeatmap } from '@/api/OperationsDashboard';
import { ChartCard } from './ChartCard';

const HEIGHT = 360;

// Pencil palette: empty/less -> more (GitHub-style buckets)
const SCALE_COLORS = ['#eef0f2', '#c5e9fb', '#7fd2f7', '#33b2f0', '#0b6fa4'];

const localDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

export const CompoundHeatmap: FC = () => {
  const { t } = useTranslate();
  const { data, isLoading, isError, refetch } = useEventHeatmap();

  const options: echarts.EChartsOption = useMemo(() => {
    const counts = new Map((data ?? []).map((d) => [d.category, d.count]));
    const max = Math.max(1, ...counts.values());

    const end = new Date();
    const start = new Date(end);
    start.setMonth(end.getMonth() - 6);
    start.setDate(start.getDate() + 1);

    // Emit a point for every day so empty days render as gray placeholders.
    const points: [string, number][] = [];
    for (
      const cursor = new Date(start);
      cursor <= end;
      cursor.setDate(cursor.getDate() + 1)
    ) {
      const iso = localDate(cursor);
      points.push([iso, counts.get(iso) ?? 0]);
    }

    return {
      tooltip: {
        position: 'top',
        formatter: (params: { value: [string, number] }) => {
          const [iso, count] = params.value;
          const label = new Date(iso).toLocaleDateString('en-GB', {
            weekday: 'short',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });
          return `${label}<br/>${count} ${t('dashboard.heatmap.events')}`;
        },
      },
      visualMap: {
        min: 0,
        max,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 12,
        itemWidth: 12,
        itemHeight: 120,
        text: [t('dashboard.heatmap.more'), t('dashboard.heatmap.less')],
        inRange: { color: SCALE_COLORS },
      },
      calendar: {
        top: 'middle',
        left: 40,
        right: 12,
        cellSize: [20, 20],
        range: [localDate(start), localDate(end)],
        itemStyle: { borderWidth: 2, borderColor: '#ffffff' },
        splitLine: { show: false },
        yearLabel: { show: false },
        dayLabel: { firstDay: 1, nameMap: 'en' },
        monthLabel: { nameMap: 'en' },
      },
      series: [{ type: 'heatmap', coordinateSystem: 'calendar', data: points }],
    };
  }, [data, t]);

  return (
    <ChartCard
      title={t('dashboard.widget.heatmap')}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      height={HEIGHT}
    >
      <ReactECharts option={options} style={{ height: HEIGHT }} />
    </ChartCard>
  );
};
